import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, TrendingDown, Minus, Dumbbell } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { API } from '../utils/api';

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const TrendBadge = ({ history }) => {
  const withWeight = history.filter((h) => typeof h.weight_kg === 'number' && h.weight_kg > 0);
  if (withWeight.length < 2) return null;
  const first = withWeight[0].weight_kg;
  const last = withWeight[withWeight.length - 1].weight_kg;
  const diff = last - first;
  const pct = first > 0 ? Math.round(((last - first) / first) * 100) : 0;
  if (Math.abs(diff) < 0.01) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 text-xs font-bold">
        <Minus className="w-3 h-3" />
        Estável
      </div>
    );
  }
  const positive = diff > 0;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
        positive ? 'bg-neon-lime/15 text-neon-lime' : 'bg-red-500/15 text-red-400'
      }`}
    >
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : ''}
      {diff.toFixed(1)}kg ({positive ? '+' : ''}
      {pct}%)
    </div>
  );
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  return (
    <div className="bg-zinc-900 border border-neon-lime/30 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">
        {item.fullDate}
      </p>
      <p className="text-white font-black text-base">
        {item.weight_kg ? `${item.weight_kg} kg` : '—'}
      </p>
      {item.reps ? (
        <p className="text-zinc-400 text-xs">{item.reps} repetições</p>
      ) : null}
    </div>
  );
};

export const ExerciseHistoryModal = ({ open, onClose, exerciseId, exerciseName, userId }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !exerciseId) return;
    let cancelled = false;
    setLoading(true);
    const url = userId
      ? `${API}/admin/users/${userId}/exercises/${exerciseId}/history?limit=20`
      : `${API}/exercises/${exerciseId}/history?limit=20`;
    axios
      .get(url)
      .then((res) => {
        if (!cancelled) setHistory(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, exerciseId, userId]);

  if (!open) return null;

  const chartData = (history || []).map((h) => ({
    date: formatDate(h.logged_date),
    fullDate: h.logged_date,
    weight_kg: h.weight_kg || 0,
    reps: h.reps || 0,
  }));

  const withWeight = (history || []).filter((h) => h.weight_kg > 0);
  const max = withWeight.length ? Math.max(...withWeight.map((h) => h.weight_kg)) : 0;
  const min = withWeight.length ? Math.min(...withWeight.map((h) => h.weight_kg)) : 0;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-xl bg-zinc-950 border border-neon-lime/20 rounded-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
          initial={{ y: 40, scale: 0.95, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 40, scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 260 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur-md border-b border-white/5 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-neon-lime/15 flex items-center justify-center flex-shrink-0">
                <Dumbbell className="w-5 h-5 text-neon-lime" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  Evolução da carga
                </p>
                <h3 className="font-primary font-black text-lg text-white truncate">
                  {exerciseName}
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0 ml-2"
              data-testid="exercise-history-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-neon-lime" />
              </div>
            ) : !history || history.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-zinc-900 mx-auto mb-4 flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-zinc-700" />
                </div>
                <p className="text-zinc-400 font-bold mb-1">
                  Nenhum registro de carga ainda
                </p>
                <p className="text-zinc-600 text-sm">
                  Registre o peso e as repetições durante o treino para ver
                  sua evolução aqui.
                </p>
              </div>
            ) : (
              <>
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                  <div className="bg-zinc-900/60 rounded-xl p-3 border border-white/5">
                    <p className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">
                      Sessões
                    </p>
                    <p className="text-white font-black text-xl mt-0.5">
                      {history.length}
                    </p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-xl p-3 border border-white/5">
                    <p className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">
                      Máx
                    </p>
                    <p className="text-neon-lime font-black text-xl mt-0.5">
                      {max ? `${max}kg` : '—'}
                    </p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-xl p-3 border border-white/5">
                    <p className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">
                      Mín
                    </p>
                    <p className="text-white font-black text-xl mt-0.5">
                      {min ? `${min}kg` : '—'}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <TrendBadge history={history} />
                </div>

                {/* Chart */}
                {withWeight.length > 0 ? (
                  <div
                    className="bg-zinc-900/40 rounded-2xl p-3 border border-white/5"
                    data-testid="exercise-history-chart"
                  >
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="#71717a"
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#71717a"
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          unit="kg"
                        />
                        <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#ccff00', strokeOpacity: 0.3 }} />
                        <Line
                          type="monotone"
                          dataKey="weight_kg"
                          stroke="#ccff00"
                          strokeWidth={3}
                          dot={{ fill: '#ccff00', r: 4, strokeWidth: 0 }}
                          activeDot={{ r: 6, fill: '#ccff00', stroke: '#000', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-zinc-500 text-sm py-6">
                    Registre o peso para ver o gráfico de evolução.
                  </p>
                )}

                {/* History list */}
                <div className="mt-5 space-y-2">
                  <p className="text-xs uppercase text-zinc-500 font-bold tracking-wider mb-2">
                    Últimos registros
                  </p>
                  {[...history].reverse().slice(0, 10).map((h, idx) => (
                    <div
                      key={`${h.logged_date}-${idx}`}
                      className="flex items-center justify-between bg-zinc-900/40 rounded-xl px-3 py-2.5 border border-white/5"
                    >
                      <span className="text-zinc-400 text-sm">{formatDate(h.logged_date)}</span>
                      <span className="text-white font-bold text-sm">
                        {h.weight_kg ? `${h.weight_kg}kg` : '—'}
                        {h.reps ? <span className="text-zinc-500 font-normal"> × {h.reps}</span> : null}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
